import React, {useMemo, MouseEvent} from 'react';
import {AttachmentType} from '../../../../components/FileUploader';
import {APIActionStatus, Column, Field, formsStore} from '../../../../stores/Form';
import {callLinkedActions} from '../../actions/screen';
import {lockScreenKeyboardNavigation} from '../../actions/lockScreenKeyboardNavigation';
import {getTabulaIni} from '../../../../constants/metaData';
import {createPriorityFileObj} from '../SidePanelForms/Attachments/utils';
import {getSrcFromU8Array} from '@priority-software/priority-utils/lib/file-processing';

type Props = {
    column: Column;
    file?: AttachmentType;
    disabled?: boolean;
};

export const useFormImageField = (props: Props) => {
    const {column, file, disabled} = props;
    const [attachment, setAttachment] = React.useState<AttachmentType>(file || null);
    const [open, setOpen] = React.useState(false);
    const uploaderAPIRef = React.useRef(null);
    const hasData = !!attachment?.Data;

    const previewSrc = useMemo(() => {
        return file?.Data && file?.MimeType ? getSrcFromU8Array(file.Data, file.MimeType) : null;
    }, [attachment]);

    const onHidePreview = () => {
        lockScreenKeyboardNavigation(false);
        setOpen(false);
    };

    const onShowPreview = e => {
        lockScreenKeyboardNavigation(true);
        setOpen(true);
    };

    const onFileUpdate = async (attachmentValue: AttachmentType) => {
        const record = column.parentForm.records[column.parentForm.currentRow];
        const field: Field = record.fieldsMap.get(column.id);

        field.setCurrentValue(attachmentValue as any); // TODO: the type should be patched in the Field class

        return await callLinkedActions(
            async () =>
                formsStore?.currentScreen?.fieldUpdate({
                    ...field
                }),
            async () => {
                setAttachment(attachmentValue);
                return APIActionStatus.passed;
            }
        );
    };

    const clearFile = async () => {
        if (disabled) return;
        await onFileUpdate({Data: null});
    };

    const getFileURL = () => {
        const resourceUrl = (window as any).MyGetURL();
        const urlData = new URL(resourceUrl);
        const origin = urlData.hostname === 'localhost' ? `https://dev.eshbel.com` : urlData.origin;
        const filePath = file?.Path?.split('system/mail/')[1] || '';
        const tabula = getTabulaIni();
        // TODO: this is a temporary solution, need to be discussed with Alex
        return `${origin}/dev/priority/primail/${tabula}/${filePath}`;
    };

    const openImage = async () => {
        window.open(getFileURL(), '_blank');
    };

    const setFile = async (files: File[]) => {
        if (disabled) return;

        const file = files[0];
        if (file) {
            const attachmentValue = await createPriorityFileObj(file);
            onFileUpdate(attachmentValue);
        }
    };

    const selectImage = async (e: MouseEvent<HTMLElement>) => {
        if (uploaderAPIRef.current) {
            uploaderAPIRef.current.openFileExplorer(e);
        }
    };

    return {
        state: {
            open,
            attachment,
            hasData,
            previewSrc
        },
        handlers: {
            setFile,
            clearFile,
            openImage,
            getFileURL,
            selectImage,
            onShowPreview,
            onHidePreview
        },
        uploaderAPIRef
    };
};
